import React, { useEffect, useRef } from 'react';
import { Card, CardContent, Grid, Typography } from '@mui/material';

type StatItem = {
  key: string;
  label: string;
  value: string | number;
  sublabel?: string;
  color: string;
};

type Props = {
  items: StatItem[];
};

function StatCard({ item }: { item: StatItem }) {
  const prevValue = useRef(item.value);
  const [pulse, setPulse] = React.useState(false);

  useEffect(() => {
    if (prevValue.current !== item.value) {
      setPulse(true);
      prevValue.current = item.value;
      const timer = window.setTimeout(() => setPulse(false), 900);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [item.value]);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        transform: pulse ? 'scale(1.02)' : 'none',
        boxShadow: pulse ? 6 : 0,
        borderColor: pulse ? item.color : 'divider',
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {item.label}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: item.color,
            lineHeight: 1.15,
            mt: 0.25,
          }}
        >
          {item.value}
        </Typography>
        {item.sublabel ? (
          <Typography variant="caption" color="text.secondary">
            {item.sublabel}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

const MonitoringStatsPanel: React.FC<Props> = ({ items }) => {
  return (
    <Grid container spacing={1.25}>
      {items.map((item) => (
        <Grid item xs={6} key={item.key}>
          <StatCard item={item} />
        </Grid>
      ))}
    </Grid>
  );
};

export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export default MonitoringStatsPanel;
